import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  CardContent,
  CardActions,
  Chip,
  Divider
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon 
} from '@mui/icons-material';
import { inspectionCycleRulesApi, ratingApi, inspectionTypesApi } from '../../services/api';

const InspectionCycleRules = ({ assetTypeId, organizationId }) => {
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [selectedInspectionType, setSelectedInspectionType] = useState('');
  const [ratingLevels, setRatingLevels] = useState([]);
  const [rules, setRules] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (assetTypeId && organizationId) {
      const fetchInspectionTypes = async () => {
        try {
          const response = await inspectionTypesApi.getTypes({ asset_type_id: assetTypeId, organization_id: organizationId });
          setInspectionTypes(response.data);
        } catch (err) {
          setError('Failed to fetch inspection types');
        }
      };
      fetchInspectionTypes();
    }
  }, [assetTypeId, organizationId]);

  useEffect(() => {
    const fetchFrequencies = async () => {
      try {
        const response = await inspectionTypesApi.getFrequencies();
        setFrequencies(response.data);
      } catch (err) {
        setError('Failed to fetch frequencies');
      }
    };
    fetchFrequencies();
  }, []);

  useEffect(() => {
    if (selectedInspectionType) {
      const fetchRulesAndRatings = async () => {
        setLoading(true);
        try {
          const rulesRes = await inspectionCycleRulesApi.getRules({ inspection_type_id: selectedInspectionType });
          setRules(rulesRes.data);

          // Assuming rating schemes are tied to asset_type_id
         const ratingSchemesRes = await ratingApi.getSchemes({ asset_type_id: assetTypeId });
         const schemesPayload = ratingSchemesRes.data;
         const schemes = Array.isArray(schemesPayload) ? schemesPayload : (schemesPayload?.data || []);

         if (schemes.length > 0) {
           const schemeId = schemes[0].id;
           const ratingLevelsRes = await ratingApi.getLevels({ scheme_id: schemeId });
           const levelsPayload = ratingLevelsRes.data;
           const levels = Array.isArray(levelsPayload) ? levelsPayload : (levelsPayload?.data || []);
           setRatingLevels(levels);
         } else {
           setRatingLevels([]);
         }
        } catch (err) {
          setError('Failed to fetch rules or rating levels');
        } finally {
          setLoading(false);
        }
      };
      fetchRulesAndRatings();
    }
  }, [selectedInspectionType, assetTypeId]);

  const handleRuleChange = async (ratingLevelId, cycleFrequency) => {
    const existingRule = rules.find(r => r.rating_level_id === ratingLevelId);
    try {
      if (existingRule) {
        if (cycleFrequency) {
          await inspectionCycleRulesApi.updateRule(existingRule.id, { cycle_frequency: cycleFrequency });
        } else {
          await inspectionCycleRulesApi.deleteRule(existingRule.id);
        }
      } else if (cycleFrequency) {
        await inspectionCycleRulesApi.createRule({
          inspection_type_id: selectedInspectionType,
          rating_level_id: ratingLevelId,
          cycle_frequency: cycleFrequency,
        });
      }
      // Refresh rules
      const rulesRes = await inspectionCycleRulesApi.getRules({ inspection_type_id: selectedInspectionType });
      setRules(rulesRes.data);
    } catch (err) {
      setError('Failed to save rule');
    }
  };

  const getRatingColor = (ratingName) => {
    const name = ratingName?.toLowerCase();
    if (name?.includes('severe') || name?.includes('critical')) return 'error';
    if (name?.includes('moderate') || name?.includes('major')) return 'warning';
    if (name?.includes('minor') || name?.includes('low')) return 'info';
    if (name?.includes('none') || name?.includes('excellent')) return 'success';
    return 'default';
  };

  const formatFrequency = (freq) => {
    if (!freq) return 'Default';
    return freq.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const selectedInspectionTypeName = inspectionTypes.find(type => type.id === selectedInspectionType)?.name;
  const activeRulesCount = rules.filter(rule => rule.cycle_frequency).length;

  return (
    <>
      <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <TimelineIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Dynamic Inspection Cycle Rules
          </Typography>
          {selectedInspectionTypeName && (
            <Chip 
              label={`${selectedInspectionTypeName} • ${activeRulesCount} rules`} 
              size="small" 
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          {/* Inspection Type Selection */}
          <FormControl fullWidth size="small">
            <InputLabel>Select Inspection Type</InputLabel>
            <Select
              value={selectedInspectionType}
              onChange={(e) => setSelectedInspectionType(e.target.value)}
              label="Select Inspection Type"
            >
              {inspectionTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon fontSize="small" color="action" />
                    {type.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {/* Rating-Based Cycle Rules */}
          {selectedInspectionType && !loading && (
            <>
              {ratingLevels.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No rating levels found for this asset type.
                </Typography>
              ) : (
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  maxHeight: '200px',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8',
                  },
                }}>
                  {ratingLevels.map((level, index) => {
                    const currentRule = rules.find(r => r.rating_level_id === level.id);
                    return (
                      <Box 
                        key={level.id} 
                        sx={{ 
                          p: 1, 
                          border: '1px solid', 
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Box sx={{ flex: 1, mr: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                              If rating is
                            </Typography>
                            <Chip 
                              label={level.name}
                              color={getRatingColor(level.name)}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                          {currentRule?.cycle_frequency && (
                            <Typography variant="caption" color="success.main">
                              ✓ {formatFrequency(currentRule.cycle_frequency)}
                            </Typography>
                          )}
                        </Box>
                        
                        <FormControl size="small" sx={{ minWidth: 90 }}>
                          <Select
                            value={currentRule?.cycle_frequency || ''}
                            onChange={(e) => handleRuleChange(level.id, e.target.value)}
                            displayEmpty
                            sx={{ fontSize: '0.875rem' }}
                          >
                            <MenuItem value="">
                              <em>Default</em>
                            </MenuItem>
                            {frequencies.map((freq) => (
                              <MenuItem key={freq} value={freq}>
                                {formatFrequency(freq)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </Stack>
      </CardContent>
      
      {selectedInspectionType && !loading && ratingLevels.length > 0 && (
        <CardActions sx={{ justifyContent: 'flex-start', pt: 0 }}>
          <Typography variant="caption" color="text.secondary">
            💡 Rules determine inspection frequency based on asset rating
          </Typography>
        </CardActions>
      )}
    </>
  );
};

export default InspectionCycleRules;